import React from 'react';
import PropTypes from 'prop-types';

import Image from '$partials/media/image';
import Button from '$partials/ui/button.jsx';

import {
    IconChevronSprite,
    IconArrowRight
} from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
* @typedef {React.FC<PropTypes.InferProps<typeof cardItemPropTypes>>}
 */
const CardItem = ({
    bentoVariant,
    body,
    cardHeight,
    cardTitle,
    classes,
    ctaLabel,
    ctaLink,
    eyebrowIcon,
    eyebrowTitle,
    image
}) => {
    return (
        bentoVariant === 'B' ?
            (
                <div className={`${cardHeight === 'Tall' ? 'aspect-square' : ''} ${classes}`}>
                    <div className='mx-10 sm:mx-5 md:mx-10 px-32 pt-50 pb-40 rounded-button flex flex-col justify-between h-full md:min-h-unset min-h-[260px] bg-sand'>
                        <div className='flex gap-15 flex-col'>
                            {image && (
                                <Image
                                    className='object-contain object-left-top h-[40px] max-w-[85px] pointer-events-none select-none'
                                    loading={''}
                                    loadWidth={140}
                                    src={image.src}
                                    style={{width: undefined}} />
                            )}
                            <p className='type-style-3'>
                                {cardTitle}
                            </p>
                        </div>
                        {ctaLabel && ctaLink && (
                            <Button
                                as='a'
                                className={`button button--tertiary-large flex items-center ${cardHeight === 'Tall' ? 'mt-auto' : ''}`}
                                href={ctaLink}>
                                <span>
                                    {ctaLabel}
                                </span>
                                <span className='ml-5'>
                                    <IconArrowRight />
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className='overflow-hidden mx-10 sm:mx-5 md:mx-10 pt-40 rounded-button flex flex-col bg-sand h-full justify-between'>
                    <div className='flex flex-col grow justify-between pb-40 px-40'>
                        <div>
                            <div className='flex flex-row items-center mb-16'>
                                {eyebrowIcon && (
                                    <div
                                        className='mr-10 pointer-events-none select-none'
                                        style={{
                                            fontSize: '18px',
                                            width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em` ,
                                            maxWidth: '100%'
                                        }}>
                                        <Image
                                            {...eyebrowIcon}
                                            loading={''}/>
                                    </div>
                                )}
                                {eyebrowTitle && (
                                    <p className='type-style-7 text-orange'>
                                        {eyebrowTitle}
                                    </p>
                                )}
                            </div>
                            <p className='type-style-3'>
                                {cardTitle}
                            </p>
                            <p className='mt-4 text-light-grey type-style-5'>
                                {body}
                            </p>
                        </div>
                        {ctaLabel && ctaLink && (
                            <div className='mt-16 flex self-start'>
                                <Button
                                    as='a'
                                    className='button button--primary'
                                    href={ctaLink}>
                                    <span>
                                        {ctaLabel}
                                    </span>
                                    <span className='button__icon button__icon--animate'>
                                        <IconChevronSprite />
                                    </span>
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className='overflow-hidden aspect-video pointer-events-none select-none'>
                        {image?.contentType?.includes('video/') ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                src={image?.src}
                                width={'100%'}/>
                        ) :
                            <Image
                                className='h-full object-cover'
                                containerClassName='h-full object-cover'
                                height={'100%'}
                                loadWidth={560}
                                src={image?.src}
                                width={'100%'}/>
                        }
                    </div>
                </div>
            )
    );
};

/**
* @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleBento = ({
    imageCardItemsCollection,
    eyebrow,
    title,
    standfirst,
    parent
}) => {
    return (
        <div className={`bg-white relative ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <div className='bg-white'>
                    {eyebrow && (
                        <h1
                            className='type-style-11 text-core-brand px-10 sm:px-5 md:px-10 mb-16'>
                            {eyebrow}
                        </h1>
                    )}
                    {title && (
                        <p className='px-10 sm:px-5 md:px-10 type-style-2 sm:w-10/12 md:w-8/12'>
                            {title}
                        </p>
                    )}
                    {standfirst && (
                        <p className='type-style-5 text-grey-off-black mt-12 px-10 sm:px-5 md:px-10 sm:w-10/12 md:w-8/12'>
                            {standfirst}
                        </p>
                    )}
                    {imageCardItemsCollection?.items?.length > 0 && (
                        imageCardItemsCollection.items.length === 3 ? (
                            <div className='flex sm:flex-row flex-col w-full mt-50'>
                                {/* Left side */}
                                <div className='sm:w-1/2'>
                                    <CardItem {...imageCardItemsCollection.items[0]} />
                                </div>

                                {/* Right side - stacked */}
                                <div className='mt-20 sm:mt-0 sm:w-1/2 flex flex-col gap-y-20'>
                                    <CardItem
                                        classes='h-1/2'
                                        {...imageCardItemsCollection.items[1]} />
                                    <CardItem
                                        classes='h-1/2'
                                        {...imageCardItemsCollection.items[2]} />
                                </div>
                            </div>
                        ) : (
                            <div className='grid sm:grid-cols-2 mt-50 gap-y-20'>
                                {imageCardItemsCollection?.items?.map((itm, i) => (
                                    <div
                                        key={i}>
                                        <CardItem
                                            key={i}
                                            {...itm} />
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const cardItemPropTypes = {
    __typename: PropTypes.string,
    bentoVariant: PropTypes.string,
    body: PropTypes.string,
    cardHeight: PropTypes.string,
    cardTitle: PropTypes.string,
    caseStudyPage: PropTypes.object,
    classes: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    eyebrowTitle: PropTypes.string,
    eyebrowIcon: PropTypes.shape({
        height: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired
    }),
    image: PropTypes.object,
    logo: PropTypes.object
};

CardItem.propTypes = cardItemPropTypes;

const propTypes = {
    eyebrow: PropTypes.string,
    imageCardItemsCollection: PropTypes.object,
    parent: PropTypes.string,
    standfirst: PropTypes.string,
    title: PropTypes.string
};

ModuleBento.propTypes = propTypes;

export default ModuleBento;
